import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, collectionGroup, query, where, getDocs, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { UserData, CompanyData, getCompanyByEmail, StaffData } from '../firebase/auth';

interface AuthContextType {
    user: User | null;
    userData: UserData | null;
    companyData: CompanyData | null;
    staffData: StaffData | null;
    loading: boolean;
    error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

// Global flag to prevent AuthContext from clearing data during staff creation
let isCreatingStaff = false;
let adminCompanyData: CompanyData | null = null;

export const setCreatingStaff = (creating: boolean, companyData?: CompanyData | null) => {
    isCreatingStaff = creating;
    if (companyData) {
        adminCompanyData = companyData;
    }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [companyData, setCompanyData] = useState<CompanyData | null>(null);
    const [staffData, setStaffData] = useState<StaffData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const staffUnsubscribeRef = React.useRef<Unsubscribe | null>(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            try {
                // If we're creating staff, preserve the admin's company data
                if (isCreatingStaff && adminCompanyData) {
                    console.log('⚠️ Staff creation in progress - preserving admin company data');
                    setCompanyData(adminCompanyData);
                    // Don't set loading to false yet - wait for staff creation to complete
                    return;
                }

                setLoading(true);
                setError(null);

                if (user) {
                    setUser(user);

                    // Fetch additional user data from Firestore
                    const userDocRef = doc(db, 'users', user.uid);
                    const userDoc = await getDoc(userDocRef);

                    if (userDoc.exists()) {
                        const data = userDoc.data() as UserData;
                        setUserData(data);
                    } else {
                        // If user data doesn't exist in Firestore, create it
                        const defaultUserData: UserData = {
                            uid: user.uid,
                            email: user.email!,
                            displayName: user.displayName || 'User',
                            role: 'client',
                            createdAt: new Date()
                        };
                        setUserData(defaultUserData);
                    }

                    // Fetch company data based on user email
                    if (user.email) {
                        const company = await getCompanyByEmail(user.email);
                        console.log('Fetched company for email:', user.email, company);
                        setCompanyData(company);

                        let foundStaff = false;

                        // Check if user is a staff member (search all companies)
                        // First try the company we found
                        let staffCompanyId: string | null = null;
                        if (company?.id) {
                            try {
                                const staffDocRef = doc(db, 'companies', company.id, 'staff', user.uid);
                                const staffDoc = await getDoc(staffDocRef);
                                if (staffDoc.exists()) {
                                    setStaffData(staffDoc.data() as StaffData);
                                    staffCompanyId = company.id;
                                    foundStaff = true;
                                }
                            } catch (err) {
                                console.error('Error fetching staff data:', err);
                            }
                        }
                        
                        // If not found in the company, search all companies for staff using collection group query
                        // This is much more efficient than querying each company individually
                        if (!foundStaff) {
                            try {
                                const staffCollectionGroup = collectionGroup(db, 'staff');
                                const staffQuery = query(staffCollectionGroup, where('uid', '==', user.uid));
                                const staffSnapshot = await getDocs(staffQuery);
                                
                                if (!staffSnapshot.empty) {
                                    const staffDoc = staffSnapshot.docs[0];
                                    const staff = staffDoc.data() as StaffData;
                                    setStaffData(staff);
                                    
                                    // Extract company ID from the document path: companies/{companyId}/staff/{staffId}
                                    const pathParts = staffDoc.ref.path.split('/');
                                    staffCompanyId = pathParts[1]; // companies/{companyId}/staff/{staffId}
                                    
                                    // Also set company data if not already set
                                    if (!company && staffCompanyId) {
                                        const companyDocRef = doc(db, 'companies', staffCompanyId);
                                        const companyDoc = await getDoc(companyDocRef);
                                        if (companyDoc.exists()) {
                                            const companyData = {
                                                id: companyDoc.id,
                                                ...companyDoc.data()
                                            } as CompanyData;
                                            setCompanyData(companyData);
                                        }
                                    }
                                    foundStaff = true;
                                }
                            } catch (err: any) {
                                console.warn('Collection group query failed, trying alternative method:', err);
                                // Fallback: search through all companies manually
                                // This is slower but works without collection group indexes
                                try {
                                    const companiesRef = collection(db, 'companies');
                                    const allCompanies = await getDocs(companiesRef);
                                    
                                    for (const companyDoc of allCompanies.docs) {
                                        try {
                                            const staffDocRef = doc(db, 'companies', companyDoc.id, 'staff', user.uid);
                                            const staffDoc = await getDoc(staffDocRef);
                                            if (staffDoc.exists()) {
                                                const staff = staffDoc.data() as StaffData;
                                                setStaffData(staff);
                                                staffCompanyId = companyDoc.id;
                                                
                                                // Also set company data if not already set
                                                if (!company && staffCompanyId) {
                                                    const companyData = {
                                                        id: companyDoc.id,
                                                        ...companyDoc.data()
                                                    } as CompanyData;
                                                    setCompanyData(companyData);
                                                }
                                                foundStaff = true;
                                                break; // Found staff, exit loop
                                            }
                                        } catch (docErr) {
                                            // Continue to next company if this one fails
                                            continue;
                                        }
                                    }
                                } catch (fallbackErr) {
                                    console.error('Error in fallback staff search:', fallbackErr);
                                }
                            }
                        }

                        // Set up real-time listener for staff data changes (permissions updates)
                        if (foundStaff && staffCompanyId) {
                            // Cleanup previous listener if exists
                            if (staffUnsubscribeRef.current) {
                                staffUnsubscribeRef.current();
                                staffUnsubscribeRef.current = null;
                            }

                            const staffDocRef = doc(db, 'companies', staffCompanyId, 'staff', user.uid);
                            const unsubscribe = onSnapshot(staffDocRef, (docSnapshot) => {
                                if (docSnapshot.exists()) {
                                    const updatedStaffData = docSnapshot.data() as StaffData;
                                    console.log('🔄 Staff permissions updated in real-time:', updatedStaffData);
                                    setStaffData(updatedStaffData);
                                } else {
                                    // Staff document was deleted
                                    setStaffData(null);
                                    if (staffUnsubscribeRef.current) {
                                        staffUnsubscribeRef.current();
                                        staffUnsubscribeRef.current = null;
                                    }
                                }
                            }, (err) => {
                                console.error('Error listening to staff data changes:', err);
                            });

                            staffUnsubscribeRef.current = unsubscribe;
                        }
                    }
                } else {
                    // User signed out - cleanup
                    if (staffUnsubscribeRef.current) {
                        staffUnsubscribeRef.current();
                        staffUnsubscribeRef.current = null;
                    }
                    setUser(null);
                    setUserData(null);
                    setCompanyData(null);
                    setStaffData(null);
                }
            } catch (err) {
                console.error('Auth state change error:', err);
                setError('Failed to load user data');
            } finally {
                setLoading(false);
            }
        });

        return () => {
            unsubscribe();
            // Cleanup staff listener on unmount
            if (staffUnsubscribeRef.current) {
                staffUnsubscribeRef.current();
                staffUnsubscribeRef.current = null;
            }
        };
    }, []);

    const value: AuthContextType = {
        user,
        userData,
        companyData,
        staffData,
        loading,
        error
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};







